import { Temporal } from 'temporal-polyfill';
import { adjustAvailabilityByTimeZone } from '../coach-availability-utils.js';
import { startOfWeekPlainDate } from '../day-of-week-utils.js';
import type { MinimalClassTime } from './class-time-shared.js';
import type { MeetingBlockLike } from './meeting-block.js';

export interface WeeklyClassTimeChange {
  /** Earliest teacher-local date among the changed weekdays in this notice. */
  firstChangedClassPlainDate: Temporal.PlainDate;
  meetingBlocks: MeetingBlockLike[];
}

/**
 * Compare the same recurring blocks in this and next source-local week. Only
 * changed wall-clock times/weekdays need a notice; a DST change shared by both
 * zones does not. Dates in the result are in the teacher's display time zone.
 * A new week automatically retires the preceding week's notices.
 */
export function upcomingWeeklyClassTimeChanges(
  classTime: MinimalClassTime,
  fromTimeZone: string,
  toTimeZone: string,
  now: Temporal.Instant = Temporal.Now.instant(),
): WeeklyClassTimeChange[] {
  if (classTime.type !== 'recurring') return [];

  const weekStart = startOfWeekPlainDate(now.toZonedDateTimeISO(fromTimeZone).toPlainDate());
  const changes = new Map<string, WeeklyClassTimeChange>();
  for (const block of classTime.meetingBlocks) {
    const convert = (referencePlainDate: Temporal.PlainDate) =>
      adjustAvailabilityByTimeZone(block, {
        sourceTimeZone: fromTimeZone,
        targetTimeZone: toTimeZone,
        referencePlainDate,
      });
    const current = convert(weekStart);
    const next = convert(weekStart.add({ weeks: 1 }));
    if (
      current.adjustedDay === next.adjustedDay &&
      current.startAdjustedPlainTime.equals(next.startAdjustedPlainTime) &&
      current.endAdjustedPlainTime.equals(next.endAdjustedPlainTime)
    )
      continue;

    const key = `${next.startAdjustedPlainTime}|${next.endAdjustedPlainTime}`;
    const change = changes.get(key) ?? { firstChangedClassPlainDate: next.startAdjustedPlainDate, meetingBlocks: [] };
    if (Temporal.PlainDate.compare(next.startAdjustedPlainDate, change.firstChangedClassPlainDate) < 0) {
      change.firstChangedClassPlainDate = next.startAdjustedPlainDate;
    }
    change.meetingBlocks.push({
      day: next.adjustedDay,
      startPlainTime: next.startAdjustedPlainTime,
      endPlainTime: next.endAdjustedPlainTime,
    });
    changes.set(key, change);
  }
  return [...changes.values()].sort((a, b) =>
    Temporal.PlainDate.compare(a.firstChangedClassPlainDate, b.firstChangedClassPlainDate),
  );
}
